<template>
  <div class="assign-people-group-form bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
    <div class="-ml-4 -mt-4 flex justify-between items-center flex-wrap sm:flex-nowrap">
      <div class="ml-4 mt-4">
        <h3 class="text-lg leading-6 font-medium text-gray-900">
          Assign to a People group
        </h3>
        <p class="mt-1 text-sm leading-5 text-gray-500" v-if="selectedCountryId">
          All of the {{ modelPrettified }}s that have been selected will be assigned to the People Group of your choice.
        </p>
      </div>
    </div>
    <div class="modal-body" v-if="selectedCountryId">
      <!-- Allow Country Selection Checkbox -->
      <div class="mb-4">
        <label class="flex items-center">
          <input
            type="checkbox"
            v-model="allowCountrySelection"
            @change="onCountrySelectionToggle"
            class="mr-2"
          />
          <span class="text-sm font-medium text-gray-700">Displaced People Groups</span>
        </label>
      </div>

      <!-- Country Select (shown when country selection is allowed) -->
      <div class="mb-4" v-show="allowCountrySelection">
        <label for="country-select" class="block text-sm font-medium text-gray-700 mb-2">Country</label>
        <multiselect
          id="country-select"
          v-model="selectedCountry"
          :options="countries || []"
          label="name"
          track-by="id"
          :placeholder="countriesPlaceholder"
          :show-labels="false"
          :multiple="false"
        />
      </div>

      <!-- People Group Select -->
      <div>
        <label for="people-group-select" class="block text-sm font-medium text-gray-700 mb-2">People Group</label>
        <multiselect
          id="people-group-select"
          v-model="selectedEntity"
          :options="peopleGroups || []"
          label="name"
          track-by="id"
          :placeholder="peopleGroupPlaceholder"
          :show-labels="false"
          :multiple="false"
          :disabled="allowCountrySelection && !selectedCountry"
        />
      </div>

      <div class="p-6 standard-form" v-if="showStartDate">
        <div class="standard-field-wrapper">
          <label for="start_date">Start Date</label>
          <div class="standard-input-wrapper">
            <div class="standard-input-container">
              <input type="text" name="start_date" ref="datepicker" v-model="startDate" id="datepicker">
            </div>
          </div>
        </div>
      </div>
      <div class="action-buttons">
        <button type="button" class="btn btn-danger" @click="cancel">Cancel</button>
        <button type="button" class="btn btn-primary" @click="submit" :disabled="!selectedEntity">Submit</button>
      </div>
    </div>
    <div class="modal-body" v-else>
      <h1> You must set a country filter to use this function </h1>
      <div class="action-buttons">
        <button type="button" class="btn btn-danger" @click="close()">Return</button>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios'
  import { startCase } from 'lodash-es'
  import Multiselect from 'vue-multiselect'
  import BulkActionService from '@services/BulkActionService'
  import FlashMessages from '@services/FlashMessages'
  import flatpickr from 'flatpickr'

  export default {
    name: 'assign-people-group-form',
    components: {
      Multiselect,
    },
    data () {
      return {
        allowCountrySelection: false,
        peopleGroups: [],
        peopleGroupsLoaded: false,
        selectedEntity: null,
        selectedCountry: null,
        selectedCountryId: null,
        countries: [],
        countriesLoaded: false,
        startDate: '',
        processing: false,
      }
    },
    computed: {
      modelPrettified () {
        return startCase(this.model)
      },
      showStartDate () {
        return this.ids.length === 1 && this.model === 'TrainingCenter'
      },
      peopleGroupPlaceholder () {
        if (this.allowCountrySelection && !this.selectedCountry) {
          return 'Select a country first'
        }
        return this.peopleGroupsLoaded ? 'Select people group' : 'Loading people groups...'
      },
      countriesPlaceholder () {
        return this.countriesLoaded ? 'Select country' : 'Loading countries...'
      }
    },
    methods: {
      requestPeopleGroups  (countryId) {
        console.log('fetching people groups for country', countryId)
        if (!countryId) {
          return Promise.resolve([])
        }
        this.peopleGroupsLoaded = false
        axios.get(`/people_groups.json?country_id=${countryId}`)
          .then(rep => {
            console.log('people groups response', rep.data)
            this.peopleGroups = Array.isArray(rep.data) ? rep.data : []
            this.peopleGroupsLoaded = true
          })
          .catch(() => {
            console.error('Error loading people groups', error)
            this.peopleGroups = []
            this.peopleGroupsLoaded = true
          })
      },
      requestCountries () {
        this.countriesLoaded = false
        axios.get('/countries.json')
          .then(rep => {
            // Handle JSONAPI format (if using CountrySerializer) or simple array format (if using jbuilder)
            let countriesData = rep.data
            if (rep.data && rep.data.data && Array.isArray(rep.data.data)) {
              // JSONAPI format - extract from data array
              countriesData = rep.data.data.map(item => ({
                id: item.id,
                name: item.attributes ? item.attributes.name : item.name,
                ...(item.attributes || {})
              }))
            } else if (Array.isArray(rep.data)) {
              // Simple array format
              countriesData = rep.data
            } else {
              countriesData = []
            }
            this.countries = countriesData
            this.countriesLoaded = true
          })
          .catch((error) => {
            console.error('Error loading countries', error)
            this.countries = []
            this.countriesLoaded = true
          })
      },
      onCountrySelectionToggle () {
        this.selectedEntity = null
        this.peopleGroups = []
        if (!this.countriesLoaded) {
          this.requestCountries()
        }
      },
      onCountryChange (country) {
        this.selectedEntity = null
        console.log('onCountryChange', country)
        if (country) {
          this.selectedCountryId = country.id
          this.requestPeopleGroups(country.id)
        } else {
          this.selectedCountryId = null
          this.peopleGroups = []
          this.peopleGroupsLoaded = true
        }
      },
      getDefaultDate () {
        axios.get(`/training_centers/${this.ids[0]}.json?`)
          .then(rep => {
            const dt = new Date(rep.data.start_date)
            this.startDate = dt.toISOString().split('T')[0]
            flatpickr(this.$refs.datepicker, { dateFormat: 'Y-m-d', defaultDate: this.startDate })
          })
      },
      cancel () {
        this.$emit('cancel')
      },
      close () {
        this.$emit('close')
      },
      submit () {
        if (!this.processing) {
          this.processing = true
          BulkActionService.assignPeopleGroup(this.ids, this.model, this.selectedEntity.id, this.startDate)
            .then(resp => {
              FlashMessages.success(`${resp.data.result} record(s) assigned to the ${this.selectedEntity.name} people group`, 'Success')
              this.close()
            })
            .catch(resp => {
              FlashMessages.error(`Failed to assign people group. Please try again later.`, 'Error')
              this.close()
            })
        }
      },
    },
    created () {
      console.log('created', this.filter)
      if (this.filter) {
        // Set selectedCountryId from filter prop (country ID)
        this.selectedCountryId = this.filter
        this.requestPeopleGroups(this.selectedCountryId)
        if (this.showStartDate) {
          this.getDefaultDate()
        }
      }
    },
    watch: {
      selectedCountry (newCountry, oldCountry) {
        console.log('selectedCountry changed', newCountry, oldCountry)
        this.onCountryChange(newCountry)
      }
    },
    props: {
      ids: {
        type: Array,
        required: true,
      },
      model: {
        type: String,
        required: true,
      },
      filter: {
        type: String,
        required: false,
      }
    },
  }
</script>

<style lang="scss">
  .assign-people-group-form {
    .modal-body {
      margin-top: 15px;

      .action-buttons {
        margin-top: 15px;
        display: flex;
        justify-content: space-between;
      }
    }
  }
</style>
